(() => {
  'use strict';

  const CONFIG_SELECTOR = '[data-llcl-variant-media-groups]';
  const PRODUCT_SECTION_SELECTOR = '.shopify-section--main-product';
  const STATE_EVENT = 'llcl:variant-media-state';
  const SHOW_ALL_EVENT = 'llcl:variant-media-show-all';

  function variantIdFromUrl() {
    const value = new URL(window.location.href).searchParams.get('variant');
    return value && /^\d+$/.test(value) ? value : null;
  }

  let modeSource = variantIdFromUrl() ? 'navigation' : 'general';
  let showAllOverride = false;

  function normalizeLabel(value) {
    return String(value || '').trim().replace(/:\s*$/, '').toLocaleLowerCase('es');
  }

  function parseConfig(root = document) {
    const node = root?.querySelector?.(CONFIG_SELECTOR) || document.querySelector(CONFIG_SELECTOR);
    if (!node) return null;

    try {
      const config = JSON.parse(node.textContent || '{}');
      return config && config.groups && config.option_name ? config : null;
    } catch (error) {
      console.warn('[LLCL variant media] Invalid configuration; Prestige fallback preserved.', error);
      return null;
    }
  }

  function findOptionFieldset(section, optionName) {
    const picker = section.querySelector('variant-picker');
    if (!picker) return null;

    const expected = normalizeLabel(optionName);
    return Array.from(picker.querySelectorAll('.variant-picker__option')).find((candidate) => {
      const legend = candidate.querySelector('legend');
      return normalizeLabel(legend?.textContent) === expected;
    }) || null;
  }

  function labelForControl(fieldset, control) {
    if (!control) return null;

    let label = control.closest('label');
    if (!label && control.id) {
      label = fieldset.querySelector(`label[for="${CSS.escape(control.id)}"]`);
    }
    if (!label) return null;

    const conciseLabel = label.querySelector('.sr-only') || label.querySelector('span');
    const value = conciseLabel?.textContent || label.textContent;
    return value?.trim() || null;
  }

  function selectedOptionValue(section, optionName) {
    const fieldset = findOptionFieldset(section, optionName);
    if (!fieldset) return null;

    const checked = fieldset.querySelector('input[type="radio"]:checked');
    const checkedLabel = labelForControl(fieldset, checked);
    if (checkedLabel) return checkedLabel;

    const selectedLabel = fieldset.querySelector('.variant-picker__option-info span');
    if (selectedLabel && selectedLabel.textContent.trim()) return selectedLabel.textContent.trim();

    const dropdownLabel = fieldset.querySelector('.select span[id$="-selected-value"]');
    if (dropdownLabel && dropdownLabel.textContent.trim()) return dropdownLabel.textContent.trim();

    return null;
  }

  function resolveGroup(config, color) {
    if (!color || !config?.groups) return null;
    if (config.groups[color]) return config.groups[color];

    const expected = normalizeLabel(color);
    const key = Object.keys(config.groups).find((candidate) => normalizeLabel(candidate) === expected);
    return key ? config.groups[key] : null;
  }

  function configuredOptionControl(section, optionName, event) {
    if (!(event.target instanceof Element)) return null;

    const option = event.target.closest('.variant-picker__option');
    if (!option || !section.contains(option)) return null;
    if (normalizeLabel(option.querySelector('legend')?.textContent) !== normalizeLabel(optionName)) return null;

    if (event.target.matches('input[type="radio"]')) return event.target;

    const label = event.target.closest('label');
    return label?.control || null;
  }

  function preserveUnitallaDisplay(section) {
    const sizeFieldset = Array.from(section.querySelectorAll('.variant-picker__option')).find((candidate) => {
      const label = normalizeLabel(candidate.querySelector('legend')?.textContent);
      return label === 'tamano' || label === 'talla' || label === 'tamaño';
    });

    if (!sizeFieldset) return;

    const legend = sizeFieldset.querySelector('legend');
    if (legend) legend.textContent = 'Talla:';

    sizeFieldset.querySelectorAll('.block-swatch span, .block-underline span, .variant-picker__option-info span').forEach((node) => {
      if (normalizeLabel(node.textContent) === 'ut') node.textContent = 'Unitalla';
    });
  }

  function mediaNumericId(value) {
    return String(value || '').split('/').pop();
  }

  function currentMode() {
    if (showAllOverride) return 'general';
    return modeSource === 'navigation' || modeSource === 'color' ? 'explicit' : 'general';
  }

  function currentSource() {
    return showAllOverride ? 'show-all' : modeSource;
  }

  function publishState(section, { color = null, filtered = false } = {}) {
    const state = {
      mode: currentMode(),
      source: currentSource(),
      variantId: variantIdFromUrl(),
      color,
      filtered
    };

    section.dataset.llclVariantMediaMode = state.mode;
    section.dataset.llclVariantMediaSource = state.source;
    section.dataset.llclVariantMediaFiltered = filtered ? 'true' : 'false';
    document.dispatchEvent(new CustomEvent(STATE_EVENT, { detail: state }));
    return state;
  }

  function showAll(gallery) {
    const carousel = gallery.carousel;
    if (!carousel || typeof carousel.filter !== 'function') return;

    carousel.filter([]);
    gallery.setAttribute('filtered-indexes', '[]');
  }

  function apply(section) {
    preserveUnitallaDisplay(section);

    const config = parseConfig(section);
    const gallery = section.querySelector('product-gallery');

    if (!config || !gallery || !gallery.carousel) return;

    if (currentMode() !== 'explicit') {
      showAll(gallery);
      publishState(section);
      return;
    }

    const color = selectedOptionValue(section, config.option_name);
    const group = resolveGroup(config, color);

    if (!group || !Array.isArray(group.ordered_media_ids) || group.ordered_media_ids.length === 0) {
      showAll(gallery);
      publishState(section, { color });
      return;
    }

    const allowedIds = new Set(group.ordered_media_ids.map(mediaNumericId));
    const allCells = gallery.carousel.allCells || [];
    const filteredIndexes = [];

    allCells.forEach((cell, index) => {
      if (!allowedIds.has(mediaNumericId(cell.dataset.mediaId))) filteredIndexes.push(index);
    });

    if (filteredIndexes.length === allCells.length) {
      showAll(gallery);
      publishState(section, { color });
      return;
    }

    gallery.carousel.filter(filteredIndexes);
    gallery.setAttribute('filtered-indexes', JSON.stringify(filteredIndexes.map((index) => index + 1)));

    const firstOrderedId = mediaNumericId(group.ordered_media_ids[0]);
    const visibleCells = gallery.carousel.cells || [];
    const targetCell = visibleCells.find((cell) => mediaNumericId(cell.dataset.mediaId) === firstOrderedId) || visibleCells[0];

    publishState(section, { color, filtered: true });

    if (!targetCell) return;

    const visibleIndex = visibleCells.indexOf(targetCell);
    if (gallery.carousel.isScrollable && visibleIndex >= 0 && typeof gallery.carousel.select === 'function') {
      gallery.carousel.select(visibleIndex, { instant: true });
    } else if (!gallery.carousel.isScrollable) {
      targetCell.scrollIntoView({ block: 'start', behavior: 'auto' });
    }
  }

  function productFormForSection(section) {
    const formId = section.querySelector('product-gallery')?.getAttribute('form');
    return formId ? document.forms[formId] : null;
  }

  function ensureExplicitVariantUrl(section) {
    if (variantIdFromUrl()) return;

    const productForm = productFormForSection(section);
    const variantId = String(productForm?.elements?.id?.value || '').trim();
    if (!/^\d+$/.test(variantId)) return;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({ path: url.toString() }, '', url.toString());
  }

  function scheduleApply(section) {
    requestAnimationFrame(() => apply(section));
  }

  function enterExplicitColorMode(section, config, event) {
    if (!event.isTrusted) return false;

    const control = configuredOptionControl(section, config.option_name, event);
    if (!control) return false;

    const wasAlreadyChecked = control.checked;
    modeSource = 'color';
    showAllOverride = false;

    if (event.type === 'click' && wasAlreadyChecked) {
      requestAnimationFrame(() => {
        ensureExplicitVariantUrl(section);
        apply(section);
      });
    }

    return true;
  }

  function productCardVariantUrl(card, variantId) {
    if (!card || !/^\d+$/.test(String(variantId || ''))) return null;

    const anchor = card.querySelector('.product-card__media, .product-title');
    if (!anchor?.href) return null;

    const url = new URL(anchor.href, window.location.origin);
    url.searchParams.set('variant', String(variantId));
    return `${url.pathname}${url.search}${url.hash}`;
  }

  function syncProductCardLinks(control) {
    const card = control?.closest?.('product-card');
    const variantId = control?.dataset?.variantId;
    const href = productCardVariantUrl(card, variantId);
    if (!card || !href) return;

    card.querySelectorAll('.product-card__media, .product-title').forEach((link) => {
      link.setAttribute('href', href);
    });

    card.dataset.llclExplicitVariantId = String(variantId);
  }

  function handleProductCardVariantIntent(event) {
    if (!(event.target instanceof Element)) return;

    const control = event.target.matches('product-card input[data-variant-id]')
      ? event.target
      : event.target.closest('label')?.control;

    if (!control?.matches?.('product-card input[data-variant-id]')) return;
    if (event.type === 'click' && !event.isTrusted) return;

    syncProductCardLinks(control);
  }

  function resetFromNavigation() {
    modeSource = variantIdFromUrl() ? 'navigation' : 'general';
    showAllOverride = false;
  }

  function configuredSections() {
    return Array.from(document.querySelectorAll(PRODUCT_SECTION_SELECTOR)).filter((section) => parseConfig(section));
  }

  function showAllMedia() {
    showAllOverride = true;
    configuredSections().forEach(apply);
  }

  function restoreExplicitMedia() {
    showAllOverride = false;
    configuredSections().forEach(apply);
  }

  function syncMediaState() {
    configuredSections().forEach(apply);
  }

  function getPublicState() {
    return {
      mode: currentMode(),
      source: currentSource(),
      variantId: variantIdFromUrl(),
      showAllOverride
    };
  }

  function init(section) {
    if (!section || section.dataset.llclVariantMediaInitialized === 'true') return;

    const config = parseConfig(section);
    if (!config) return;

    section.dataset.llclVariantMediaInitialized = 'true';

    const productForm = productFormForSection(section);

    section.addEventListener('click', (event) => {
      enterExplicitColorMode(section, config, event);
    }, true);

    section.addEventListener('change', (event) => {
      enterExplicitColorMode(section, config, event);
    }, true);

    if (productForm) {
      productForm.addEventListener('variant:change', () => scheduleApply(section));
      productForm.addEventListener('product:rerender', () => scheduleApply(section));
    }

    scheduleApply(section);
  }

  function boot() {
    document.querySelectorAll(PRODUCT_SECTION_SELECTOR).forEach(init);
  }

  window.LLCLVariantMedia = Object.assign(window.LLCLVariantMedia || {}, {
    getState: getPublicState,
    restoreExplicit: restoreExplicitMedia,
    showAll: showAllMedia,
    sync: syncMediaState
  });

  document.addEventListener('click', handleProductCardVariantIntent, true);
  document.addEventListener('change', handleProductCardVariantIntent, true);
  document.addEventListener(SHOW_ALL_EVENT, showAllMedia);

  window.addEventListener('popstate', () => {
    resetFromNavigation();
    syncMediaState();
  });

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    resetFromNavigation();
    syncMediaState();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('product:rerender', () => requestAnimationFrame(boot));
})();
